import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fundInvestorsAPI } from '../../services/api';
import { Users, Download, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const FundInvestorsTab = ({ fundId, fundName, fundCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');

  useEffect(() => {
    fetchInvestors();
  }, [fundId]);

  const fetchInvestors = async () => {
    try {
      const res = await fundInvestorsAPI.getByFund(fundId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch fund investors:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = data?.investors?.filter(inv => {
    const matchesSearch =
      (inv.account_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.folio_no || '').toLowerCase().includes(search.toLowerCase());
    const matchesClass = !classFilter || (inv.class_of_units || []).includes(classFilter);
    return matchesSearch && matchesClass;
  }) || [];

  // Numbers in this tab are always shown in the fund's native currency,
  // so the comma-grouping locale must follow that currency (en-IN for INR,
  // en-US otherwise). Previously every number used en-IN even for USD funds,
  // producing Indian-lakh grouping like "2,63,00,000" instead of "26,300,000".
  const fmtLocale = (fundCurrency || 'INR') === 'INR' ? 'en-IN' : 'en-US';
  const fmt = (val) => {
    if (val === null || val === undefined) return '-';
    return Number(val).toLocaleString(fmtLocale, { maximumFractionDigits: 2 });
  };

  const handleExport = () => {
    if (!filtered.length) return;
    const ws = XLSX.utils.json_to_sheet(filtered.map(r => ({
      'Account Name': r.account_name,
      'Contact Name': r.contact_name,
      'Type': r.type_of_investor,
      'Folio No': r.folio_no,
      'Class of Units': (r.class_of_units || []).join(', '),
      'Commitment': r.commitment_amount,
      'Called': r.called_amount,
      'Contribution': r.contribution_amount,
      'Distribution': r.distribution_amount,
      'NAV': r.nav_value,
      'IRR (%)': r.investor_irr,
    })));

    // Add a second sheet for the class-wise summary so the export mirrors the on-screen tables.
    if ((data?.class_breakdown || []).length > 0) {
      const cbWs = XLSX.utils.json_to_sheet((data.class_breakdown || []).map(c => ({
        'Class': c.class_name,
        'Commitment': c.commitment,
        'Called': c.called,
        'Contribution': c.contribution,
        'Distribution': c.distribution,
        'NAV': c.nav,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Fund Investors');
      XLSX.utils.book_append_sheet(wb, cbWs, 'Class-wise Summary');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fundName || 'Fund'}_Investors.xlsx`);
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fund Investors');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fundName || 'Fund'}_Investors.xlsx`);
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading investors...</div>;
  if (!data || !data.investors?.length) {
    return (
      <div className="py-12 text-center" data-testid="fund-investors-empty">
        <Users size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">No investors found for this fund.</p>
        <p className="text-sm text-gray-400 mt-1">Investor accounts with investments in this fund will appear here.</p>
      </div>
    );
  }

  const classBreakdown = data.class_breakdown || [];
  const allClassNames = Array.from(new Set([
    ...classBreakdown.map(c => c.class_name),
    ...(data.investors || []).flatMap(i => i.class_of_units || []),
  ])).filter(Boolean);

  return (
    <div data-testid="fund-investors-tab">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-blue-600 font-medium">Total Investors</p>
          <p className="text-xl font-bold text-blue-900" data-testid="fund-investors-count">{data.total_investors}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-green-600 font-medium">Total Commitment</p>
          <p className="text-lg font-bold text-green-900">{fundCurrency} {fmt(data.totals.commitment)}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4" data-testid="fund-investors-called-tile">
          <p className="text-xs text-orange-600 font-medium">Total Called</p>
          <p className="text-lg font-bold text-orange-900">{fundCurrency} {fmt(data.totals.called)}</p>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4">
          <p className="text-xs text-indigo-600 font-medium">Total Contribution</p>
          <p className="text-lg font-bold text-indigo-900">{fundCurrency} {fmt(data.totals.contribution)}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <p className="text-xs text-amber-600 font-medium">Total Distribution</p>
          <p className="text-lg font-bold text-amber-900">{fundCurrency} {fmt(data.totals.distribution)}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-purple-600 font-medium">Total NAV</p>
          <p className="text-lg font-bold text-purple-900">{fundCurrency} {fmt(data.totals.nav)}</p>
        </div>
      </div>

      {/* Class-wise breakdown */}
      {classBreakdown.length > 0 && (
        <div className="mb-6 bg-white border rounded-lg overflow-hidden" data-testid="class-breakdown-table">
          <div className="px-4 py-2 bg-gray-50 border-b">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Class-wise Summary</p>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Commitment</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Called</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Contribution</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Distribution</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">NAV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {classBreakdown.map(cb => (
                <tr key={cb.class_name} className="hover:bg-gray-50" data-testid={`class-row-${cb.class_name}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{cb.class_name}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(cb.commitment)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(cb.called)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(cb.contribution)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(cb.distribution)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(cb.nav)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search + Class filter + Export */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, folio..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="fund-investors-search"
            />
          </div>
          {allClassNames.length > 0 && (
            <select
              value={classFilter}
              onChange={e => setClassFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="fund-investors-class-filter"
            >
              <option value="">All Classes</option>
              {allClassNames.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          data-testid="fund-investors-export"
        >
          <Download size={16} />
          Export Excel
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200" data-testid="fund-investors-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio No</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class of Units</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commitment</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Called</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contribution</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Distribution</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">NAV</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IRR (%)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((inv, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium">
                  <Link to={`/investors/accounts/${inv.account_id}`} className="text-blue-600 hover:underline" data-testid={`fund-investor-link-${idx}`}>
                    {inv.account_name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{inv.contact_name || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{inv.type_of_investor || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{inv.folio_no || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {(inv.class_of_units || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {(inv.class_of_units || []).map(c => (
                        <span key={c} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded font-medium">{c}</span>
                      ))}
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(inv.commitment_amount)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono" data-testid={`fund-investor-called-${idx}`}>{fmt(inv.called_amount)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(inv.contribution_amount)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(inv.distribution_amount)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(inv.nav_value)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{inv.investor_irr || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FundInvestorsTab;
